import React, { useState, useEffect, useLayoutEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, useSearchParams, useLocation } from 'react-router-dom';
import { Container, Text, Box, Paper, Group, Avatar, Textarea, Button, Stack, Loader, Center, useMantineColorScheme, useComputedColorScheme, useMantineTheme } from '@mantine/core';
import { IconSend, IconArrowLeft } from '@tabler/icons-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useMenu } from '../hooks/useMenu';
import { useMenuColorScheme } from '../hooks/useMenuColorScheme';
import { useMenuChat } from '../hooks/useMenuChat';
import { useMenuAuth } from '../hooks/useMenuAuth';
import { MenuItem } from '../components/menu/MenuItem';
import { MenuItemModal } from '../components/menu/MenuItemModal';
import { CartModal } from '../components/menu/CartModal';
import CartButton from '../components/menu/CartButton';
import { useCart } from '../context/CartContext';
import { showAddedToCartNotification } from '../utils/notifications';
import { MenuItem as MenuItemType, ChatMessage } from '../types/menu';
import { getDisplayText as getTranslationDisplay } from '../utils/translationHelpers';
import { detectBrowserLanguage } from '../utils/browserLanguageDetector';
import { t } from '../utils/uiTranslations';
import { setSkipThemePersist } from '../utils/themePersist';

// --- Styles ---
// Message bubble styles - now a function to support dark mode
const getMessageBubbleStyle = (isUser: boolean, isDarkMode: boolean) => ({
    maxWidth: '85%',
    alignSelf: isUser ? 'flex-end' as const : 'flex-start' as const,
    backgroundColor: isUser ? '#7950f2' : (isDarkMode ? '#25262b' : 'white'), // Violet for user, dark/white for AI
    color: isUser ? 'white' : (isDarkMode ? '#c1c2c5' : 'black'),
    padding: '12px 16px',
    borderRadius: '16px',
    borderBottomRightRadius: isUser ? '4px' : '16px',
    borderBottomLeftRadius: isUser ? '16px' : '4px',
    // Conditional shadow for dark mode - white glow for better visibility
    boxShadow: isDarkMode 
      ? (isUser 
          ? '0 1px 2px rgba(255, 255, 255, 0.1), 0 1px 1px rgba(0, 0, 0, 0.2)' // White glow for user messages
          : '0 1px 2px rgba(255, 255, 255, 0.1), 0 1px 1px rgba(0, 0, 0, 0.2)') // White glow for AI messages
      : '0 1px 2px rgba(0,0,0,0.05)', // Original shadow for light mode
});

// Styles are now functions that accept theme and isDarkMode to support dark mode
// dark[9] is the darkest color in Mantine's palette (#101113)
const getStyles = (theme: any, isDarkMode: boolean) => ({
    pageContainer: {
        height: '100vh',
        display: 'flex',
        flexDirection: 'column' as const,
        backgroundColor: isDarkMode ? theme.colors.dark[9] : '#f8f9fa',
    },
    header: {
        padding: '16px',
        backgroundColor: isDarkMode ? theme.colors.dark[7] : 'white',
        borderBottom: `1px solid ${isDarkMode ? theme.colors.dark[4] : '#e9ecef'}`,
        zIndex: 10,
    },
    chatContainer: {
        flex: 1,
        overflowY: 'auto' as const,
        padding: '16px',
        display: 'flex',
        flexDirection: 'column' as const,
        gap: '16px',
        // Keep chat container at dark[7] in dark mode (current color)
        backgroundColor: isDarkMode ? theme.colors.dark[7] : 'transparent',
    },
    inputContainer: {
        padding: '16px',
        backgroundColor: isDarkMode ? theme.colors.dark[7] : 'white',
        borderTop: `1px solid ${isDarkMode ? theme.colors.dark[4] : '#e9ecef'}`,
    },
    cardContainer: {
        maxWidth: '100%',
        width: '100%',
        marginTop: '4px',
        marginBottom: '4px',
    }
});

// --- Helper Hooks ---
const useLanguageToggle = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    // Initialize from URL so ?lang=jp is used immediately (chat API gets correct language from first message)
    const [currentLanguage, setCurrentLanguage] = useState(() => {
        const lang = searchParams.get('lang');
        if (lang && lang !== 'source') return lang;
        if (lang === 'source') return 'jp';
        return 'en';
    });

    useEffect(() => {
        const langParam = searchParams.get('lang');
        if (langParam && langParam !== 'source') {
            setCurrentLanguage(langParam);
        } else if (langParam === 'source') {
            setCurrentLanguage('jp');
        } else {
            const savedLanguage = localStorage.getItem('menuLanguagePreference');
            if (savedLanguage && (savedLanguage === 'en' || savedLanguage === 'jp')) {
                setCurrentLanguage(savedLanguage);
            } else {
                const browserLanguage = detectBrowserLanguage();
                setCurrentLanguage(browserLanguage);
            }
        }
    }, [searchParams]);

    const getDisplayText = useCallback((item: MenuItemType, field: 'name' | 'description' | 'disclaimer') => {
        const sourceLanguage = item.props?.sourceLanguage || 'jp';

        // disclaimer has no top-level field — always read from translations
        if (field === 'disclaimer') {
            return item.props?.translations?.[currentLanguage]?.disclaimer?.text || '';
        }

        // SOURCE LANGUAGE: Prefer cleaned translation (※ notes removed) if available, then top-level field.
        if (currentLanguage === sourceLanguage) {
            const cleaned = item.props?.translations?.[currentLanguage]?.[field]?.text;
            if (cleaned !== undefined && cleaned !== null && String(cleaned).trim() !== '') {
                return String(cleaned);
            }
            const topLevel = item[field];
            if (topLevel !== undefined && topLevel !== null && String(topLevel).trim() !== '') {
                return String(topLevel);
            }
            return item[field] ?? '';
        }

        // PRIMARY: Try new translations structure first
        const newTranslation = item.props?.translations?.[currentLanguage]?.[field]?.text;
        if (newTranslation) return newTranslation;

        // SECONDARY: Try direct translation for English (prefer props.translations.en; legacy enLocalization fallback)
        if (currentLanguage === 'en') {
            if (field === 'name') return item.props?.translations?.en?.name?.text ?? item.props?.enLocalization?.name ?? item.name;
            if (field === 'description') return item.props?.translations?.en?.description?.text ?? item.props?.enLocalization?.description ?? item.description;
        }

        return item[field] || '';
    }, [currentLanguage]);

    return { currentLanguage, getDisplayText };
};


// --- Components ---

const ChatMessageBubble: React.FC<{
    message: ChatMessage;
    menuItemsMap: Map<string, MenuItemType>;
    getDisplayText: (item: MenuItemType, field: 'name' | 'description' | 'disclaimer') => string;
    onItemSelect: (itemId: string) => void;
    addItem: (item: MenuItemType) => void;
    currentLanguage: string;
    isInitialMessage?: boolean;
    /** When false, clicking a menu item card opens the modal only (no add to cart). */
    cartEnabled?: boolean;
}> = React.memo(({ message, menuItemsMap, getDisplayText, onItemSelect, addItem, currentLanguage, isInitialMessage = false, cartEnabled = true }) => {
    const isUser = message.sender === 'user';
    const colorScheme = useComputedColorScheme('light', { getInitialValueInEffect: true });
    const isDarkMode = colorScheme === 'dark';

    // Debug: Log the raw message text
    console.log('📝 [MENU_CHAT] Rendering message:', {
        sender: message.sender,
        textLength: message.text?.length,
        textPreview: message.text?.substring(0, 200),
        hasMenuItemLinks: message.text?.includes('menu_item:')
    });

    // Custom renderer for links to handle menu items
    const components = useMemo(() => ({
        a: ({ node, children, href, ...props }: any) => {
            console.log('🔗 [MENU_CHAT] Link renderer called:', { href, children, props });

            // Handle explicit menu_item:ID links
            if (href && href.startsWith('menu_item:')) {
                const itemId = href.replace('menu_item:', '');
                console.log('✅ [MENU_CHAT] Detected menu_item link:', itemId);
                const item = menuItemsMap.get(itemId);
                console.log('📦 [MENU_CHAT] Item lookup result:', item ? 'FOUND' : 'NOT FOUND', item?.name);

                if (item) {
                    console.log('🎴 [MENU_CHAT] Rendering MenuItem card for:', item.name);
                    return (
                        <Box
                            style={{
                                maxWidth: '100%',
                                width: '100%',
                                marginTop: '4px',
                                marginBottom: '4px',
                            }}
                            onClick={(e) => {
                                e.stopPropagation();
                                console.log('🖱️ [MENU_CHAT] Card clicked:', item.name, item.id);
                                if (cartEnabled) {
                                    addItem(item);
                                    showAddedToCartNotification(getDisplayText(item, 'name'));
                                } else {
                                    onItemSelect(item.id);
                                }
                            }}
                        >
                            <MenuItem
                                item={item}
                                getDisplayText={getDisplayText}
                                onItemSelect={(id) => {
                                    console.log('🎯 [MENU_CHAT] MenuItem onItemSelect called:', id);
                                    onItemSelect(id);
                                }}
                            />
                        </Box>
                    );
                }
                console.warn('⚠️ [MENU_CHAT] Item not found for ID:', itemId);
                return <Text c="red" size="sm">Item not found</Text>;
            }

            // Handle potential hallucinated links (e.g. /menus/123/chat)
            // If the link text matches a menu item name, render the card
            if (href && (href.startsWith('/menus/') || href.startsWith('http'))) {
                console.log('🔄 [MENU_CHAT] Detected standard link, attempting name match:', href);
                const linkText = String(children).trim();
                // Try to find item by name
                const itemByName = Array.from(menuItemsMap.values()).find(item =>
                    item.name === linkText ||
                    item.props?.translations?.en?.name?.text === linkText ||
                    item.props?.enLocalization?.name === linkText ||
                    getDisplayText(item, 'name') === linkText
                );

                if (itemByName) {
                    console.log('✅ [MENU_CHAT] Found item by name match:', itemByName.name);
                    return (
                        <Box style={{
                            maxWidth: '100%',
                            width: '100%',
                            marginTop: '4px',
                            marginBottom: '4px',
                        }}>
                            <MenuItem
                                item={itemByName}
                                getDisplayText={getDisplayText}
                                onItemSelect={onItemSelect}
                            />
                        </Box>
                    );
                }
                console.log('❌ [MENU_CHAT] No item found by name for:', linkText);
            }

            console.log('🌐 [MENU_CHAT] Rendering standard link:', href);
            return <a href={href} {...props} target="_blank" rel="noopener noreferrer" style={{ color: isUser ? 'white' : 'green', textDecoration: 'underline' }}>{children}</a>;
        },
        p: ({ children }: any) => <Text size="sm" style={{ margin: 0, lineHeight: 1.5, marginBottom: '8px' }}>{children}</Text>,
        ul: ({ children }: any) => <ul style={{ margin: '8px 0', paddingLeft: '20px', listStyleType: 'disc' }}>{children}</ul>,
        ol: ({ children }: any) => <ol style={{ margin: '8px 0', paddingLeft: '20px' }}>{children}</ol>,
        li: ({ children }: any) => <li style={{ marginBottom: '4px', lineHeight: 1.5 }}>{children}</li>
    }), [menuItemsMap, getDisplayText, onItemSelect, isUser, cartEnabled]);

    return (
        <Box 
            style={getMessageBubbleStyle(isUser, isDarkMode)}
            data-message-id={message.id}
        >
            {!isUser && (
                <Group gap="xs" mb={4} align="center">
                    <Avatar size="xs" radius="xl" color="blue">AI</Avatar>
                    <Text size="xs" c="dimmed">Haru</Text>
                </Group>
            )}
            <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={components}
                urlTransform={(url) => {
                    // Preserve menu_item: protocol and other custom protocols
                    console.log('🔧 [MENU_CHAT] urlTransform called:', url);
                    return url;
                }}
            >
                {isInitialMessage ? t('chatInitialMessage', currentLanguage) : message.text}
            </ReactMarkdown>
            {!isUser && !isInitialMessage && (
                <Text
                    size="xs"
                    c="dimmed"
                    style={{
                        marginTop: '4px',
                        textAlign: 'right',
                        fontStyle: 'italic'
                    }}
                >
                    {t('createdByAi', currentLanguage)}
                </Text>
            )}
        </Box>
    );
});

const MenuChat: React.FC = () => {
    const { menuId } = useParams<{ menuId: string }>();
    const [searchParams, setSearchParams] = useSearchParams();
    const location = useLocation();
    const { menu, loading, error } = useMenu(menuId!);
    const theme = useMantineTheme();
    const colorScheme = useComputedColorScheme('light', { getInitialValueInEffect: true });
    const isDarkMode = colorScheme === 'dark';
    
    // Get dynamic styles based on theme and dark mode
    const styles = getStyles(theme, isDarkMode);
    
    // Fetch color scheme early (before full menu loads) to prevent white flash
    // This hook makes a lightweight API call to get just the color scheme
    const { colorScheme: menuColorScheme } = useMenuColorScheme(menuId);
    
    const { currentLanguage, getDisplayText } = useLanguageToggle();
    const { addItem, getTotalItems } = useCart();

    const {
        messages,
        newMessage,
        setNewMessage,
        isSending,
        handleSendMessage,
        sendInitialMessage
    } = useMenuChat(menu, currentLanguage);

    const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
    const [cartOpened, setCartOpened] = useState(false);
    const scrollRef = useRef<HTMLDivElement>(null);

    // Apply restaurant color scheme IMMEDIATELY when color scheme data is available
    // useLayoutEffect runs synchronously before browser paint to prevent flashing
    const { setColorScheme } = useMantineColorScheme();
    useLayoutEffect(() => {
        // Only set color scheme if we're still on a menu chat route
        // This prevents setting dark mode when navigating away to admin
        if (!location.pathname.startsWith('/menus/')) {
            return; // Don't set color scheme if we're not on a menu route
        }
        
        // Use the color scheme from the lightweight endpoint
        // This is available much faster than waiting for the full menu to load
        const targetColorScheme = menuColorScheme;

        // Don't persist menu theme so other tabs don't flicker and next load stays correct
        setSkipThemePersist(true);
        setColorScheme(targetColorScheme);
        document.documentElement.setAttribute('data-mantine-color-scheme', targetColorScheme);
        setSkipThemePersist(false);
    }, [menuColorScheme, location.pathname, setColorScheme]);
    
    // Note: We don't reset color scheme on unmount here because ColorSchemeManager
    // in App.tsx handles route-based color scheme changes immediately when routes change.
    // This prevents flashing during navigation.

    // Track the previous messages length to detect new messages
    const previousMessagesLengthRef = useRef<number>(0);
    
    // Scroll to the top of the newly generated message when it arrives
    useEffect(() => {
        // Only scroll if a new message was added (length increased)
        if (messages.length > previousMessagesLengthRef.current && scrollRef.current) {
            const container = scrollRef.current;
            
            // Get the last message ID
            const lastMessage = messages[messages.length - 1];
            const lastMessageId = lastMessage?.id;
            
            // Small delay to ensure DOM has updated with the new message
            setTimeout(() => {
                if (lastMessageId) {
                    // Find the message element by its data attribute
                    const lastMessageElement = container.querySelector(`[data-message-id="${lastMessageId}"]`) as HTMLElement;
                    
                    if (lastMessageElement) {
                        // Scroll to the top of the last message element
                        lastMessageElement.scrollIntoView({
                            behavior: 'smooth',
                            block: 'start', // Align to top of the viewport
                            inline: 'nearest'
                        });
                    } else {
                        // Fallback: if we can't find the element, scroll to the last message's approximate position
                        const allMessages = container.children;
                        if (allMessages.length > 0) {
                            const lastMessageWrapper = allMessages[allMessages.length - 1] as HTMLElement;
                            if (lastMessageWrapper) {
                                const messageTop = lastMessageWrapper.offsetTop;
                                container.scrollTo({
                                    top: messageTop - 10, // Small offset to show a bit above the message
                                    behavior: 'smooth'
                                });
                            }
                        }
                    }
                }
            }, 100); // Small delay to ensure DOM is ready
            
            // Update ref for next comparison
            previousMessagesLengthRef.current = messages.length;
        } else {
            // Update the previous length even if we didn't scroll
            previousMessagesLengthRef.current = messages.length;
        }
    }, [messages]);

    // Send initial message
    useEffect(() => {
        if (menu && !loading) {
            sendInitialMessage();
        }
    }, [menu, loading, sendInitialMessage]);

    // Create a map of menu items for O(1) lookup
    const menuItemsMap = useMemo(() => {
        const map = new Map<string, MenuItemType>();
        if (menu?.menuPages) {
            menu.menuPages.forEach(page => {
                page.menuItems.forEach(item => {
                    map.set(item.id, item);
                });
            });
        }
        return map;
    }, [menu]);

    const handleItemSelect = useCallback((itemId: string) => {
        console.log('🎯 [MENU_CHAT] handleItemSelect called with ID:', itemId);
        setSelectedItemId(itemId);
        console.log('✅ [MENU_CHAT] selectedItemId state updated to:', itemId);
    }, []);

    const handleCloseModal = useCallback(() => {
        setSelectedItemId(null);
    }, []);

    const handleCartButtonClick = useCallback(() => {
        setCartOpened(true);
    }, []);

    const handleCartModalClose = useCallback(() => {
        setCartOpened(false);
    }, []);

    const selectedMenuItem = useMemo(() => {
        return selectedItemId ? menuItemsMap.get(selectedItemId) : null;
    }, [selectedItemId, menuItemsMap]);

    // Cart bar and add-to-order visibility: respect restaurant setting (default on)
    const cartEnabled = menu?.restaurant?.cartEnabled !== false;

    if (loading) return <Center h="100vh"><Loader /></Center>;
    if (error) return <Center h="100vh"><Text c="red">{error}</Text></Center>;
    if (!menu) return <Center h="100vh"><Text>Menu not found</Text></Center>;

    return (
        <Box style={styles.pageContainer}>
            {/* Header */}
            <Box style={styles.header}>
                <Group>
                    <Button
                        variant="subtle"
                        size="xs"
                        leftSection={<IconArrowLeft size={16} />}
                        component="a"
                        href={`/menus/${menuId}`}
                        c="dark"
                    >
                        Back to Menu
                    </Button>
                    <Text fw={600} size="lg" lineClamp={1}>
                        {t('chatWithHaru', currentLanguage)} @ {menu.restaurant?.name}
                    </Text>
                </Group>
            </Box>

            {/* Chat Area */}
            <Box style={styles.chatContainer} ref={scrollRef}>
                {messages.map((msg, index) => (
                    <ChatMessageBubble
                        key={msg.id}
                        message={msg}
                        menuItemsMap={menuItemsMap}
                        getDisplayText={getDisplayText}
                        onItemSelect={handleItemSelect}
                        addItem={addItem}
                        currentLanguage={currentLanguage}
                        isInitialMessage={index === 0 && msg.sender === 'ai'}
                        cartEnabled={cartEnabled}
                    />
                ))}
                {isSending && (
                    <Box style={getMessageBubbleStyle(false, isDarkMode)}>
                        <Group gap="xs" align="center">
                            <Avatar size="xs" radius="xl" color="blue">AI</Avatar>
                            <Text size="sm" c="dimmed">Thinking...</Text>
                        </Group>
                    </Box>
                )}
            </Box>

            {/* Input Area */}
            <Box style={styles.inputContainer}>
                <Group align="flex-end">
                    <Textarea
                        placeholder="Ask about the menu..."
                        autosize
                        minRows={1}
                        maxRows={4}
                        style={{ flex: 1 }}
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.currentTarget.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                handleSendMessage();
                            }
                        }}
                    />
                    <Button
                        onClick={handleSendMessage}
                        loading={isSending}
                        disabled={!newMessage.trim()}
                        radius="md"
                    >
                        <IconSend size={18} />
                    </Button>
                </Group>
            </Box>

            {/* Item Modal */}
            {selectedMenuItem && (
                <MenuItemModal
                    opened={!!selectedMenuItem}
                    onClose={handleCloseModal}
                    item={selectedMenuItem}
                    getDisplayText={getDisplayText}
                    menuId={menu?.id}
                    restaurantName={menu?.restaurant?.name}
                    restaurantDescription={menu?.restaurant?.description}
                    currentLanguage={currentLanguage}
                    cartEnabled={cartEnabled}
                />
            )}

            {/* Cart Button - only when cart is enabled for this restaurant */}
            {cartEnabled && (
                <CartButton
                    itemCount={getTotalItems()}
                    onClick={handleCartButtonClick}
                />
            )}

            {/* Cart Modal - only when cart is enabled */}
            {cartEnabled && (
                <CartModal
                    opened={cartOpened}
                    onClose={handleCartModalClose}
                />
            )}
        </Box>
    );
};

export default MenuChat;
